/**
 * Bottom sheet for editing/deleting a Telegram user
 */
import { useState } from 'react';
import { Save, Trash2, AlertTriangle, UserCheck, UserX } from 'lucide-react';
import { BottomSheet } from '@/shared/ui';
import type { TelegramUser } from '@/shared/api/admin-users-api';
import {
  useUpdateTelegramUser,
  useDeleteTelegramUser,
} from '../hooks/useAdminUsers';
import { haptic } from '@/shared/utils/telegram';
import { useAuth } from '@/shared/hooks/useAuth';
import { BrandSubscriptionSelector } from './BrandSubscriptionSelector';
import { AffiliateSubscriptionSelector } from './AffiliateSubscriptionSelector';

interface EditUserSheetProps {
  isOpen: boolean;
  onClose: () => void;
  user: TelegramUser;
}

export function EditUserSheet({ isOpen, onClose, user }: EditUserSheetProps) {
  const [role, setRole] = useState(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateMutation = useUpdateTelegramUser();
  const deleteMutation = useDeleteTelegramUser();
  const { user: currentUser } = useAuth();

  const isPending = user.status === 'pending';
  const isApproved = user.status === 'approved';
  const hasChanges = role !== user.role || isActive !== user.isActive;

  // Check if editing self
  const isEditingSelf = currentUser?.telegramUserId === user.telegramUserId;

  // Check if trying to disable self
  const isTryingToDisableSelf = isEditingSelf && !isActive && user.isActive;

  const handleSave = async () => {
    if (!hasChanges) {
      haptic.notification('warning');
      return;
    }

    try {
      haptic.impact('medium');
      await updateMutation.mutateAsync({
        telegramUserId: user.telegramUserId,
        data: { role, isActive },
      });
      haptic.notification('success');
      onClose();
    } catch (error) {
      haptic.notification('error');
    }
  };

  const handleDeleteClick = () => {
    haptic.impact('light');
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      haptic.impact('heavy');
      await deleteMutation.mutateAsync(user.telegramUserId);
      haptic.notification('success');
      onClose();
    } catch (error) {
      haptic.notification('error');
    }
  };

  const handleDeleteCancel = () => {
    haptic.impact('light');
    setShowDeleteConfirm(false);
  };

  const handleApprove = async () => {
    try {
      haptic.impact('medium');
      await updateMutation.mutateAsync({
        telegramUserId: user.telegramUserId,
        data: { status: 'approved', isActive: true, role: 'viewer' },
      });
      haptic.notification('success');
      onClose();
    } catch {
      haptic.notification('error');
    }
  };

  const handleReject = async () => {
    try {
      haptic.impact('heavy');
      await updateMutation.mutateAsync({
        telegramUserId: user.telegramUserId,
        data: { status: 'rejected', isActive: false },
      });
      haptic.notification('success');
      onClose();
    } catch {
      haptic.notification('error');
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Edit User">
      {!showDeleteConfirm ? (
        <div className="space-y-6">
          {/* User ID Display */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
              Telegram User ID
            </label>
            <div className="h-11 px-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl flex items-center">
              <span className="text-sm font-mono font-bold text-[var(--tg-theme-text-color)]">
                {user.telegramUserId}
              </span>
            </div>
          </div>

          {/* Username/Name Display */}
          {(user.username || user.firstName) && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
                Profile
              </label>
              <div className="h-11 px-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl flex items-center">
                <span className="text-sm font-medium text-[var(--tg-theme-text-color)]">
                  {user.username && `@${user.username}`}
                  {user.username && (user.firstName || user.lastName) && ' · '}
                  {user.firstName} {user.lastName}
                </span>
              </div>
            </div>
          )}

          {/* Pending User: Approve/Reject Buttons */}
          {isPending && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-bold text-amber-600 mb-1">Pending Approval</p>
                  <p className="text-amber-600/80">
                    This user registered via /start and is waiting for approval.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleApprove}
                  disabled={updateMutation.isPending}
                  className="flex-1 py-3 px-4 bg-emerald-600 text-white rounded-xl text-sm font-bold uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <UserCheck size={18} />
                  {updateMutation.isPending ? 'Approving...' : 'Approve'}
                </button>
                <button
                  onClick={handleReject}
                  disabled={updateMutation.isPending}
                  className="flex-1 py-3 px-4 bg-rose-600 text-white rounded-xl text-sm font-bold uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <UserX size={18} />
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Approved User: Full Edit Controls */}
          {!isPending && (
            <>
              {/* Role Select */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
                  className="w-full h-11 px-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
                >
                  <option value="viewer">Viewer (read-only)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between py-2">
                <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
                  Active Status
                </label>
                <button
                  type="button"
                  onClick={() => setIsActive(!isActive)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    isActive ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      isActive ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Brand Subscriptions (for approved users) */}
              {isApproved && (
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
                    Brand Subscriptions
                  </label>
                  <p className="text-[10px] text-[var(--tg-theme-hint-color)]">
                    Select brands this user will receive DM notifications for
                  </p>
                  <BrandSubscriptionSelector telegramUserId={user.telegramUserId} />
                </div>
              )}

              {/* Affiliate Subscriptions (for approved users) */}
              {isApproved && (
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
                    Affiliate Subscriptions
                  </label>
                  <p className="text-[10px] text-[var(--tg-theme-hint-color)]">
                    Select affiliate IDs this user can access (events filtered by affiliate)
                  </p>
                  <AffiliateSubscriptionSelector telegramUserId={user.telegramUserId} />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || updateMutation.isPending}
                  className="flex-1 py-3 px-4 bg-[var(--tg-theme-link-color)] text-white rounded-xl text-sm font-bold uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>

                <button
                  onClick={handleDeleteClick}
                  disabled={deleteMutation.isPending}
                  className="py-3 px-4 bg-rose-500/10 text-rose-600 rounded-xl text-sm font-bold uppercase tracking-wide hover:bg-rose-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </>
          )}

          {/* Warning for editing self */}
          {isEditingSelf && (
            <div className="flex items-start gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl">
              <AlertTriangle size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-bold text-rose-600 mb-1">Editing Yourself</p>
                <p className="text-rose-600/80">
                  You are editing your own account. Be careful not to lock yourself out!
                </p>
              </div>
            </div>
          )}

          {/* Warning for disabling self */}
          {isTryingToDisableSelf && (
            <div className="flex items-start gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl">
              <AlertTriangle size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-bold text-rose-600 mb-1">Self-Lockout Warning</p>
                <p className="text-rose-600/80">
                  You are about to disable your own account. You will be immediately logged out and unable to access the app!
                </p>
              </div>
            </div>
          )}

          {/* Warning for admin role */}
          {user.role === 'admin' && !isEditingSelf && (
            <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-bold text-amber-600 mb-1">Admin Account</p>
                <p className="text-amber-600/80">
                  This user has admin privileges. Be careful when modifying their access.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Delete Confirmation */
        <div className="space-y-6 py-4">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-rose-500/10 flex items-center justify-center">
              <AlertTriangle size={32} className="text-rose-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-bold text-[var(--tg-theme-text-color)] mb-2">
                Delete User?
              </h3>
              <p className="text-sm text-[var(--tg-theme-hint-color)]">
                Are you sure you want to delete user{' '}
                <span className="font-mono font-bold">{user.telegramUserId}</span>?
              </p>
              {isEditingSelf && (
                <p className="text-xs text-rose-600 font-bold mt-2">
                  ⚠️ You are deleting your own account! You will be immediately logged out!
                </p>
              )}
              {user.role === 'admin' && !isEditingSelf && (
                <p className="text-xs text-amber-600 font-bold mt-2">
                  ⚠️ This is an admin account!
                </p>
              )}
              <p className="text-xs text-[var(--tg-theme-hint-color)] mt-2">
                This action cannot be undone.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDeleteCancel}
              className="flex-1 py-3 px-4 bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] rounded-xl text-sm font-bold uppercase tracking-wide hover:opacity-80 transition-opacity"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="flex-1 py-3 px-4 bg-rose-600 text-white rounded-xl text-sm font-bold uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
